import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { LabelSelect } from '../../../components/Input/Input';
import {
  ENTRY_TYPE,
  INSTALLMENT_STATUS,
  isAccountPlanCompatible,
} from '../../../core/constants/financial';
import { loadAccountPlans } from '../../../core/utils/accountPlan';
import { loadFinancialEntriesByPeriodAndType } from '../../../core/utils/financialEntry';
import { getRangeFromFilter, todayISO } from '../../../core/utils/financialPeriod';
import { formatCurrency, formatDateBR } from '../../../core/utils/format';
import { useTableSort } from '../../../core/hooks/useTableSort';
import SortableTh from './SortableTh';
import FinancialEntryModal from './FinancialEntryModal';
import FinancialListModal from './FinancialListModal';
import PeriodFilter from './PeriodFilter';

// Ordena por Data de Emissão (mais recente → mais antiga).
const byIssueDateDesc = (a, b) =>
  (b.issue_date || '').localeCompare(a.issue_date || '');

const SORT_ACCESSORS = {
  emissao: (e) => e.issue_date || '',
  parte: (e) => e.supplier_client || '',
  plano: (e) => e.AccountPlan?.name || '',
  documento: (e) => e.document || '',
  parcelas: (e) => (e.Installment || []).length,
  valor: (e) => Number(e.total_value) || 0,
};

// Modal de detalhamento (Receitas ou Despesas). Por LANÇAMENTO em regime de
// competência (issue_date, soma de total_value) — MESMA base do card e da tela
// de Lançamentos (Ajuste 08). O total daqui bate com o card clicado. Clicar na
// linha abre o FinancialEntryModal para edição (igual ao Extrato).
const FinancialEntriesDetailModal = ({ isOpen, onClose, periodRange, type, onChanged }) => {
  const company = useSelector((state) => state.app.company || {});

  const [entries, setEntries] = useState([]);
  const [accountPlans, setAccountPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [filter, setFilter] = useState('periodo');
  const [customRange, setCustomRange] = useState({ from: todayISO(), to: todayISO() });
  const [editEntryId, setEditEntryId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const isReceita = type === ENTRY_TYPE.RECEITA;
  // Card/detalhe usam o tipo do PLANO DE CONTAS: receita, ou tudo que sai
  // (dedução + custo + despesa) — mesma regra do loadEntriesSummary.
  const accent = isReceita ? '#70E000' : '#e88c2a';
  const title = isReceita ? 'Receitas do Período' : 'Despesas do Período';
  const partyLabel = isReceita ? 'Cliente' : 'Fornecedor';

  const range = getRangeFromFilter(filter, customRange);

  // Ao abrir, inicia no período vindo do dashboard; o usuário pode trocar.
  useEffect(() => {
    if (!isOpen) return;
    setFilter('periodo');
    setCustomRange({
      from: periodRange?.from || todayISO(),
      to: periodRange?.to || todayISO(),
    });
    setSearch('');
    setPlanFilter('');
    setEditEntryId(null);
  }, [isOpen, type, periodRange?.from, periodRange?.to]);

  useEffect(() => {
    if (!isOpen || !company.id || !type) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      loadFinancialEntriesByPeriodAndType(company.id, range, type),
      loadAccountPlans(company.id),
    ])
      .then(([data, plans]) => {
        if (cancelled) return;
        setEntries(data || []);
        setAccountPlans(plans || []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, company.id, type, range.from, range.to, refreshKey]);

  const planMap = useMemo(() => {
    const m = new Map();
    accountPlans.forEach((p) => m.set(p.id, p));
    return m;
  }, [accountPlans]);

  // Para despesas, agrupa pelo level-1 (pai). Para receitas usa o próprio plano.
  const groupForPlan = (planId) => {
    const plan = planMap.get(planId);
    if (!plan) return null;
    if (plan.parent_id && planMap.has(plan.parent_id)) {
      return planMap.get(plan.parent_id);
    }
    return plan;
  };

  const filterOptions = useMemo(() => {
    const map = new Map();
    entries.forEach((entry) => {
      if (!isAccountPlanCompatible(entry.AccountPlan?.type, type)) return;
      const planId = entry.account_plan_id;
      if (!planId) return;
      const target = isReceita ? planMap.get(planId) : groupForPlan(planId);
      if (target && !map.has(target.id)) {
        map.set(target.id, {
          value: target.id,
          label: `${target.code || ''} ${target.name || ''}`.trim(),
        });
      }
    });
    const opts = Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
    return [
      { value: '', label: isReceita ? 'Todos os planos' : 'Todos os grupos' },
      ...opts,
    ];
  }, [entries, planMap, isReceita]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = entries.filter((entry) => {
      if (!isAccountPlanCompatible(entry.AccountPlan?.type, type)) return false;
      const planId = entry.account_plan_id;
      if (planFilter) {
        if (isReceita) {
          if (planId !== planFilter) return false;
        } else {
          const target = groupForPlan(planId);
          if (target?.id !== planFilter) return false;
        }
      }
      if (!term) return true;
      const party = (entry.supplier_client || '').toLowerCase();
      const doc = (entry.document || '').toLowerCase();
      const plan = (entry.AccountPlan?.name || '').toLowerCase();
      const desc = (entry.description || '').toLowerCase();
      return (
        party.includes(term) ||
        doc.includes(term) ||
        plan.includes(term) ||
        desc.includes(term)
      );
    });
    return result.sort(byIssueDateDesc);
  }, [entries, search, planFilter, planMap, isReceita]);

  const { sorted, sort, toggleSort } = useTableSort(filtered, SORT_ACCESSORS);

  const kpiTotal = filtered.reduce((s, e) => s + (Number(e.total_value) || 0), 0);
  const kpiCount = filtered.length;
  const kpiTicket = kpiCount > 0 ? kpiTotal / kpiCount : 0;
  const kpiMaior =
    kpiCount > 0
      ? Math.max(...filtered.map((e) => Number(e.total_value) || 0))
      : 0;

  const paidCount = (entry) =>
    (entry.Installment || []).filter((i) => i.status === INSTALLMENT_STATUS.PAGO)
      .length;

  const filterSelect = (
    <div className="fd-modal-filter">
      <LabelSelect
        menuPortalTarget={document.body}
        options={filterOptions}
        value={filterOptions.find((o) => o.value === planFilter)}
        placeholder={isReceita ? 'Todos os planos' : 'Todos os grupos'}
        onChange={(opt) => setPlanFilter(opt?.value || '')}
      />
    </div>
  );

  return (
    <>
    <FinancialListModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      badge={kpiCount}
      searchPlaceholder={`Buscar ${partyLabel.toLowerCase()} ou plano...`}
      search={search}
      onSearchChange={setSearch}
      headerExtra={filterSelect}
    >
      <PeriodFilter
        filter={filter}
        onFilterChange={setFilter}
        customRange={customRange}
        onCustomRangeChange={setCustomRange}
      />

      <div className="fd-modal-summary">
        <div className="fd-modal-summary-item">
          <span className="fd-modal-summary-label" style={{ color: accent }}>
            {isReceita ? 'Total Receitas' : 'Total Despesas'}
          </span>
          <span className="fd-modal-summary-value" style={{ color: accent }}>
            {formatCurrency(kpiTotal)}
          </span>
        </div>
        <div className="fd-modal-summary-item">
          <span className="fd-modal-summary-label" style={{ color: '#60b4f6' }}>
            Lançamentos
          </span>
          <span className="fd-modal-summary-value" style={{ color: '#60b4f6' }}>
            {kpiCount}
          </span>
        </div>
        <div className="fd-modal-summary-item">
          <span className="fd-modal-summary-label" style={{ color: '#e88c2a' }}>
            Ticket Médio
          </span>
          <span className="fd-modal-summary-value" style={{ color: '#e88c2a' }}>
            {formatCurrency(kpiTicket)}
          </span>
        </div>
        <div className="fd-modal-summary-item fd-modal-summary-item--last">
          <span className="fd-modal-summary-label">
            {isReceita ? 'Maior Receita' : 'Maior Despesa'}
          </span>
          <span className="fd-modal-summary-value">{formatCurrency(kpiMaior)}</span>
        </div>
      </div>

      <div className="fd-modal-table-wrapper">
        <table className="fd-modal-table fd-modal-table--clickable">
          <thead>
            <tr>
              <SortableTh label="Emissão" sortKey="emissao" sort={sort} onSort={toggleSort} style={{ width: 100 }} />
              <SortableTh label={partyLabel} sortKey="parte" sort={sort} onSort={toggleSort} />
              <SortableTh label="Plano de Contas" sortKey="plano" sort={sort} onSort={toggleSort} />
              {!isReceita && <th style={{ width: 140 }}>Grupo</th>}
              <SortableTh label="Documento" sortKey="documento" sort={sort} onSort={toggleSort} style={{ width: 110 }} />
              <SortableTh label="Parcelas" sortKey="parcelas" sort={sort} onSort={toggleSort} style={{ width: 90, textAlign: 'center' }} />
              <SortableTh label="Valor" sortKey="valor" sort={sort} onSort={toggleSort} style={{ width: 130, textAlign: 'right' }} />
            </tr>
          </thead>
          <tbody>
            {sorted.map((entry) => {
              const grupo = !isReceita ? groupForPlan(entry.account_plan_id) : null;
              const total = (entry.Installment || []).length;
              return (
                <tr
                  key={entry.id}
                  onClick={() => setEditEntryId(entry.id)}
                  title="Clique para editar"
                >
                  <td>{formatDateBR(entry.issue_date)}</td>
                  <td>{entry.supplier_client || '—'}</td>
                  <td className="fd-table-muted">{entry.AccountPlan?.name || '—'}</td>
                  {!isReceita && (
                    <td className="fd-table-muted">{grupo?.name || '—'}</td>
                  )}
                  <td className="fd-table-muted">{entry.document || '—'}</td>
                  <td className="fd-table-muted" style={{ textAlign: 'center' }}>
                    {paidCount(entry)}/{total}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: accent }}>
                    {formatCurrency(entry.total_value)}
                  </td>
                </tr>
              );
            })}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={isReceita ? 6 : 7} className="fd-table-empty">
                  Nenhum lançamento encontrado
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={isReceita ? 6 : 7} className="fd-table-empty">
                  Carregando...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </FinancialListModal>

    <FinancialEntryModal
      isOpen={editEntryId != null}
      entryId={editEntryId}
      onClose={() => setEditEntryId(null)}
      onSaved={() => {
        setRefreshKey((k) => k + 1);
        if (onChanged) onChanged();
      }}
    />
    </>
  );
};

export default FinancialEntriesDetailModal;
